import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import store from '@/store';
import { Question, QuestionNoMath } from './question.model';
import { ErrorResponse } from '@/utils/response';
import { axios, CustomAxiosResponse } from '@/utils/axios';
import { BuildQuestion } from '@/utils/add-question/add-question-util';

export enum SaveQuestionEvent {
  Update = 'update',
  Publish = 'publish',
  Unpublish = 'unpublish',
  CreateDaft = 'create_draft',
  CreatePublish = 'create_publish',
}

export interface FetchQuestionParams {
  id: number;
}

export interface PreviewQuestionParams {
  id: number;
  buildQuestion: BuildQuestion;
}

export interface SaveQuestionParams {
  id: number;
  buildQuestion: BuildQuestion;
  event: SaveQuestionEvent;
}

export interface DeleteQuestionParams {
  questionId: number;
  isSubjective: boolean;
}

export interface QuestionStoreInterface {
  question: QuestionNoMath | null;
  mathQuestion: Question | null;
  isSubjective: boolean;
  questionId: number;
  error: ErrorResponse | null;
  fetchQuestion(params: FetchQuestionParams): Promise<void>;
  previewQuestion(params: PreviewQuestionParams): Promise<void>;
  saveQuestion(params: SaveQuestionParams): Promise<void>;
  changeIsSubjective(params: boolean): Promise<void>;
  changeQuestionId(params: number): Promise<void>;
  deleteQuestion(params: DeleteQuestionParams): Promise<void>;
}

@Module({
  namespaced: true,
  dynamic: true,
  name: 'question',
  store,
})
class QuestionStore extends VuexModule {
  question: QuestionNoMath | null = null;
  mathQuestion: Question | null = null;
  isSubjective = false;
  questionId = 0;
  error: ErrorResponse | null = null;

  @Mutation
  updateQuestion(params: QuestionNoMath) {
    this.question = params;
  }

  @Mutation
  UpdateMathQuestion(params: Question) {
    this.mathQuestion = params;
  }

  @Mutation
  updateIsSubjective(params: boolean) {
    this.isSubjective = params;
  }

  @Mutation
  updateQuestionId(params: number) {
    if (!params) {
      this.questionId = 0;
    } else {
      this.questionId = params;
    }
  }

  @Mutation
  setError(params: ErrorResponse) {
    this.error = params;
  }

  @Action
  async changeIsSubjective(params: boolean): Promise<void> {
    this.context.commit('updateIsSubjective', params);
  }

  @Action
  async changeQuestionId(params: number): Promise<void> {
    this.context.commit('updateQuestionId', params);
  }

  @Action
  async fetchQuestion(params: FetchQuestionParams): Promise<void> {
    const { id } = params;

    this.context.commit('updateQuestion', null);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(`questions/${id}`, {
        params: {
          is_subjective: this.isSubjective,
        },
      });

      this.context.commit('updateQuestion', data.question);
      this.context.commit('UpdateMathQuestion', data.mathQuestion);
    } catch (error) {
      this.context.commit('updateQuestion', null);
      this.context.commit('setError', error);
    }
  }

  @Action
  async previewQuestion(params: PreviewQuestionParams): Promise<void> {
    const { id, buildQuestion } = params;

    this.context.commit('updateQuestion', null);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.post(
        `questions/preview`,
        {
          questionId: id,
          isSubjective: this.isSubjective,
          buildQuestion,
        },
      );

      this.context.commit('updateQuestion', data.question);
      this.context.commit('UpdateMathQuestion', data.mathQuestion);
      this.context.commit('updateQuestionId', data.question.id);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async saveQuestion(params: SaveQuestionParams): Promise<void> {
    const { id, buildQuestion, event } = params;

    this.context.commit('updateQuestion', null);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.post(`questions/save`, {
        questionId: id,
        isSubjective: this.isSubjective,
        buildQuestion,
        event,
      });

      this.context.commit('updateQuestion', data.question);
      this.context.commit('UpdateMathQuestion', data.mathQuestion);
      this.context.commit('updateQuestionId', data.question.id);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async deleteQuestion(params: DeleteQuestionParams): Promise<void> {
    this.context.commit('setError', null);

    const { questionId, isSubjective } = params;

    try {
      await axios.delete(`questions/${questionId}`, {
        data: { isSubjective },
      });
    } catch (error) {
      this.context.commit('setError', error);
    }
  }
}

export default getModule(QuestionStore);
